import { useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getFaqItems, createFaqItem, updateFaqItem } from '../../lib/api'
import { faqItemSchema } from '../../lib/schemas'
import { Input } from '../../components/ui/Input'
import { Toggle } from '../../components/ui/Toggle'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import { RichTextEditor } from '../../components/ui/RichTextEditor'

export default function FaqForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: items } = useQuery({ queryKey: ['faq'], queryFn: getFaqItems, enabled: isEdit })
  const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
    resolver: zodResolver(faqItemSchema),
    defaultValues: { question: '', answer: '', order: 0, isActive: true },
  })

  useEffect(() => {
    if (isEdit && items) {
      const item = items.find(f => f.id === id)
      if (item) reset(item)
    }
  }, [items, id, isEdit, reset])

  const mutation = useMutation({
    mutationFn: (data) => isEdit ? updateFaqItem(id, data) : createFaqItem(data),
    onSuccess: (result) => {
      qc.setQueryData(['faq'], old =>
        isEdit
          ? (old ?? []).map(i => i.id === id ? result : i)
          : [...(old ?? []), result]
      )
      toast.success(isEdit ? 'FAQ updated' : 'FAQ created')
      navigate('/faq')
    },
    onError: (e) => toast.error(e.message),
  })

  if (isEdit && !items) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>

  return (
    <div className="">
      <div className="flex items-center gap-3 mb-8">
        <Link to="/faq"><Button variant="ghost" size="sm"><i className="ti ti-arrow-left" /></Button></Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit FAQ' : 'New FAQ'}</h1>
          <p className="text-sm text-gray-500 mt-0.5">Frequently asked question</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-6">
        <section className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Question</h2>
          <Input label="Question" placeholder="How do I list my business?" error={errors.question?.message} {...register('question')} />
        </section>

        <section>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Answer</h2>
          <Controller name="answer" control={control} render={({ field }) => (
            <RichTextEditor placeholder="Write a detailed answer..." value={field.value} onChange={field.onChange} error={errors.answer?.message} />
          )} />
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Settings</h2>
          <Input label="Sort order" type="number" placeholder="0" error={errors.order?.message} {...register('order')} />
          <Controller name="isActive" control={control} render={({ field }) => (
            <Toggle label="Active" description="Show on the public site" checked={field.value} onChange={field.onChange} />
          )} />
        </section>

        <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
          <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
            {isEdit ? 'Update FAQ' : 'Create FAQ'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/faq')}>Cancel</Button>
        </div>
      </form>
    </div>
  )
}
